'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildQuerySummary } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useMemo, useRef } from 'react';
import { useEngine } from '../EngineContext';
import { QuerySummarySkeleton } from '../Skeleton/QuerySummarySkeleton';
import { hasSearchQuery } from '../hooks/hasSearchQuery';
import { QuerySummaryProps } from './QuerySummary.types';

const TITLE_PREFIX_KEY = 'search.page-title-prefix';

/**
 * Read the `q` parameter from the URL hash fragment as a fallback when the
 * Coveo QuerySummary controller does not expose `state.query` (this can
 * happen on deep-link navigations where the URL manager restores search
 * parameters but the controller state is not yet updated).
 */
function getQueryFromHash(): string {
  if (typeof window === 'undefined') return '';
  try {
    return new URLSearchParams(window.location.hash.slice(1)).get('q') ?? '';
  } catch {
    return '';
  }
}

export const QuerySummary = ({
  variant = 'full',
  className,
}: QuerySummaryProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const eng = useEngine();
  const controller = useMemo(() => buildQuerySummary(eng), [eng]);
  const state = useController(controller);
  const hasSearched = useRef(false);

  if (state.firstSearchExecuted) {
    hasSearched.current = true;
  }

  const getMicrocopy = (key: string, fallback: string) => {
    const entry = searchLabelsMicrocopy?.find((m) => m.key === key);
    return entry?.value ?? fallback;
  };

  if (!state.hasResults && hasSearched.current) {
    return null;
  }

  // Show skeleton only during initial load, before the first search
  if (!state.firstSearchExecuted) {
    // Title variant only renders when there's a search query — skip skeleton
    // if no q= in the URL hash to avoid a layout shift when it disappears
    if (variant === 'title' && !hasSearchQuery()) return null;
    return <QuerySummarySkeleton variant={variant} className={className} />;
  }

  if (!state.hasResults) {
    return null;
  }

  const resultCountText = `Results ${state.firstResult}–${state.lastResult} of ${state.total}`;

  const searchQuery = state.query || getQueryFromHash();
  const titlePrefix = getMicrocopy(TITLE_PREFIX_KEY, 'Search results for');

  const titleElement = searchQuery ? (
    <>
      <span className="font-normal">{titlePrefix} </span>
      <strong>&quot;{searchQuery}&quot;</strong>
    </>
  ) : null;

  if (variant === 'title') {
    if (!titleElement) return null;
    return (
      <h1 className={className} data-test="search-page-title">
        {titleElement}
      </h1>
    );
  }

  if (variant === 'count') {
    return (
      <p className={className} data-test="query-summary-count">
        {resultCountText}
      </p>
    );
  }

  // Full variant (default) — used on mobile
  return (
    <div className={className} data-test="query-summary">
      {titleElement && (
        <h1 className="header-mid mb-4 lg:mb-6" data-test="search-page-title">
          {titleElement}
        </h1>
      )}
      <p className="body-small text-grey-600" data-test="query-summary-count">
        {resultCountText}
      </p>
    </div>
  );
};
